import type { AxiosResponse } from "axios";

import { HttpApiService } from "@/shared/services/httpApi.service";

import type { Card } from "../types/cards-api.types";
import type {
  CreatePocketRequest,
  Pocket,
  PocketBalanceResponse,
  PocketsApiResponse,
  UpdatePocketRequest,
} from "../types/pockets-api.types";
import type { WalletTransactionsResponse } from "../types/wallets-api.types";

const BASE = "/internal/pockets";

export type PocketTransferType = "topup" | "withdrawal";

export type PocketTransferRequest = {
  type: PocketTransferType;
  amount: number;
  currency?: "USD";
};

export type PocketTransferResponse = {
  success: boolean;
  operation: PocketTransferType;
  amount: number;
  currency: string;
  pocket_id: string;
  accounting_status: "completed" | "pending_reconciliation";
  tracking_id?: string;
};

// Org-scoping is enforced server-side from the JWT — never pass
// `organization_id` from here.
export class PocketsService {
  static async fetchPockets(params?: {
    limit?: number;
    skip?: number;
    withAggregates?: boolean;
  }): Promise<AxiosResponse<PocketsApiResponse>> {
    const qs: string[] = [];
    if (params?.limit != null) qs.push(`limit=${params.limit}`);
    if (params?.skip != null) qs.push(`skip=${params.skip}`);
    if (params?.withAggregates) qs.push(`with_aggregates=true`);
    const url = qs.length ? `${BASE}?${qs.join("&")}` : BASE;
    return HttpApiService.get<PocketsApiResponse>(url);
  }

  static async fetchVisibility(): Promise<AxiosResponse<{ visible: boolean }>> {
    return HttpApiService.get<{ visible: boolean }>(`${BASE}/visibility`);
  }

  static async getPocketById(id: string): Promise<AxiosResponse<Pocket>> {
    return HttpApiService.get<Pocket>(`${BASE}/${id}`);
  }

  static async createPocket(body: CreatePocketRequest): Promise<AxiosResponse<Pocket>> {
    return HttpApiService.post<Pocket>(BASE, body);
  }

  static async updatePocket(id: string, body: UpdatePocketRequest): Promise<AxiosResponse<Pocket>> {
    return HttpApiService.patch<Pocket>(`${BASE}/${id}`, body);
  }

  // Soft-deletes the pocket. The backend rejects (400) when the pocket has a
  // non-zero balance or linked cards.
  static async deletePocket(id: string): Promise<AxiosResponse<void>> {
    return HttpApiService.delete<void>(`${BASE}/${id}`);
  }

  static async getPocketBalance(id: string): Promise<AxiosResponse<PocketBalanceResponse>> {
    return HttpApiService.get<PocketBalanceResponse>(`${BASE}/${id}/balance`);
  }

  static async getPocketCards(id: string): Promise<AxiosResponse<Card[]>> {
    return HttpApiService.get<Card[]>(`${BASE}/${id}/cards`);
  }

  static async getPocketTransactions(
    id: string,
    params?: { limit?: number; skip?: number },
  ): Promise<AxiosResponse<WalletTransactionsResponse>> {
    const qs: string[] = [];
    if (params?.limit != null) qs.push(`limit=${params.limit}`);
    if (params?.skip != null) qs.push(`skip=${params.skip}`);
    const url = qs.length ? `${BASE}/${id}/transactions?${qs.join("&")}` : `${BASE}/${id}/transactions`;
    return HttpApiService.get<WalletTransactionsResponse>(url);
  }

  // On success the caller may see `accounting_status:
  // "pending_reconciliation"` when the provider moved funds but the
  // COMPLETED promotion failed.
  static async transferPocket(
    id: string,
    body: PocketTransferRequest,
  ): Promise<AxiosResponse<PocketTransferResponse>> {
    return HttpApiService.post<PocketTransferResponse>(`${BASE}/${id}/transfer`, body);
  }
}
